import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, Clock, Wand2, LayoutDashboard, Trophy, Aperture, Check, Compass, Menu } from "lucide-react";
import { ROTATING_PLACEHOLDERS } from "../lib/placeholders";
import { getHistory, mergeServerHistory } from "../lib/history";
import { captureRefFromUrl, getStoredRefCode } from "../lib/referral";
import { findOnThisDay } from "../lib/onThisDay";
import { getMyMoodcards, inferPersonalize } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { track } from "../lib/track";
import ThemeToggle from "../components/ThemeToggle";
import AuthButton from "../components/AuthButton";
import PersonalizePanel from "../components/PersonalizePanel";
import PackPicker from "../components/PackPicker";
import OnThisDay from "../components/OnThisDay";
import ReferralCard from "../components/ReferralCard";
import MoodLanes from "../components/MoodLanes";
import CardOfTheDay from "../components/CardOfTheDay";
import ChallengeTile from "../components/ChallengeTile";
import MobileTopBar from "../components/mobile/MobileTopBar";
import HamburgerSheet from "../components/mobile/HamburgerSheet";
import MobileCreate from "../components/mobile/MobileCreate";
import { getMyQuota, getMyCardOpens } from "../lib/api";

const PERSONALIZE_KEY = "moodcard.personalize.v1";
const AI_KEY = "moodcard.useai.v1";

export default function Home() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isPremium } = useAuth();
    // Restore the typed feeling when Loading bounces back after a failure —
    // user shouldn't have to retype just because our card-writer hiccupped.
    const [feeling, setFeeling] = useState(() => location.state?.restore_feeling || "");
    // One-shot guard so the auto-scroll fires only after the user actually
    // starts typing (NOT on the autoFocus that lands when the page loads —
    // that would jump the headline offscreen on every visit).
    const scrolledRef = React.useRef(false);
    const [phIdx, setPhIdx] = useState(0);
    const [historyCount, setHistoryCount] = useState(0);
    const [onThisDay, setOnThisDay] = useState(null);
    const [useAI, setUseAI] = useState(true);
    const [moodLanesOpen, setMoodLanesOpen] = useState(false);
    const [moodLanesLabel, setMoodLanesLabel] = useState(null);
    const [moodLanesSuggestion, setMoodLanesSuggestion] = useState(null);  // Phase 3·a — pre-light chips
    const [quota, setQuota] = useState(null);     // { tier, used, limit, remaining }
    const [personalize, setPersonalize] = useState({
        audience: null,
        tone: null,
        locality: "",
        tags: [],
    });
    // Style Pack v2 — pre-generation pack picker. Free users pick from 4
    // packs recommended by the inferred lane; premium users get every pack.
    // Stored as a pack id (e.g. "cinematic") OR null to let the backend
    // auto-pick from the lane.
    const [shapeChoice, setShapeChoice] = useState(null);
    // iter-72 (e): challenge tagging. If user came in from /challenge/:slug
    // via "write your version" CTA, the slug is in ?challenge=... so we can
    // tag the resulting moodcard after creation.
    const [activeChallengeSlug, setActiveChallengeSlug] = useState(null);
    // iter-74: mobile hamburger sheet open state.
    const [hamburgerOpen, setHamburgerOpen] = useState(false);

    // Prefill feeling + challenge slug from URL params on first render.
    // /challenge/:slug -> "write your version" -> /?prompt=...&challenge=...
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const promptParam = params.get("prompt");
        const challengeParam = params.get("challenge");
        if (promptParam) setFeeling(promptParam);
        if (challengeParam) setActiveChallengeSlug(challengeParam);
    }, []);  // intentionally one-shot — only consume URL params on mount

    // Refresh quota on mount + whenever user changes
    useEffect(() => {
        getMyQuota().then(setQuota).catch(() => setQuota(null));
    }, [user?.user_id]);

    // Fresh-opens probe — when logged-in user has card opens NEWER than the
    // last time they visited Dashboard, the LayoutDashboard icon pulses amber.
    // Uses localStorage "moodcard.dashboard.seen_until" timestamp so the
    // pulse vanishes the moment they open Dashboard. Silent on failure.
    const [hasFreshOpens, setHasFreshOpens] = useState(false);
    useEffect(() => {
        if (!user?.user_id) { setHasFreshOpens(false); return; }
        let cancelled = false;
        getMyCardOpens(5)
            .then((items) => {
                if (cancelled || !items?.length) return;
                let seenUntil = 0;
                try { seenUntil = Number(window.localStorage.getItem("moodcard.dashboard.seen_until") || 0); } catch { /* ignore */ }
                const latest = items.reduce((max, it) => {
                    const t = new Date(it.last_opened_at || it.opened_at || 0).getTime();
                    return t > max ? t : max;
                }, 0);
                setHasFreshOpens(latest > seenUntil);
            })
            .catch(() => { /* silent */ });
        return () => { cancelled = true; };
    }, [user?.user_id]);

    // Reply context — when the user landed here from clicking "send a feeling
    // back to <Name>" on a public card, we surface that intent here AND
    // persist it through the create flow via sessionStorage. Cleared after
    // a successful send from the ShareSheet.
    const [replyTo, setReplyTo] = useState(() => {
        try {
            return location.state?.reply_to
                || window.sessionStorage.getItem("moodcard.reply_to")
                || null;
        } catch { return null; }
    });
    useEffect(() => {
        const incoming = location.state?.reply_to;
        if (incoming) {
            setReplyTo(incoming);
            try { window.sessionStorage.setItem("moodcard.reply_to", incoming); } catch { /* ignore */ }
        }
    }, [location.state]);
    const clearReply = () => {
        setReplyTo(null);
        try {
            window.sessionStorage.removeItem("moodcard.reply_to");
            window.sessionStorage.removeItem("moodcard.reply_from_slug");
        } catch { /* ignore */ }
    };

    useEffect(() => {
        captureRefFromUrl();
        track("home_view");
        const id = setInterval(() => {
            setPhIdx((i) => (i + 1) % ROTATING_PLACEHOLDERS.length);
        }, 3200);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        const h = getHistory();
        setHistoryCount(h.length);
        setOnThisDay(findOnThisDay(h));
        try {
            const raw = localStorage.getItem(PERSONALIZE_KEY);
            if (raw) setPersonalize({ audience: null, tone: null, locality: "", tags: [], ...JSON.parse(raw) });
        } catch { /* ignore */ }
        try {
            const ai = localStorage.getItem(AI_KEY);
            if (ai !== null) setUseAI(ai === "1");
        } catch { /* ignore */ }
    }, []);

    // Pull server-side history when authenticated
    useEffect(() => {
        if (!user) return;
        getMyMoodcards(50).then((items) => {
            const merged = mergeServerHistory(items);
            setHistoryCount(merged.length);
            setOnThisDay(findOnThisDay(merged));
        }).catch(() => {});
    }, [user]);

    useEffect(() => {
        try { localStorage.setItem(PERSONALIZE_KEY, JSON.stringify(personalize)); } catch { /* ignore */ }
    }, [personalize]);

    useEffect(() => {
        try { localStorage.setItem(AI_KEY, useAI ? "1" : "0"); } catch { /* ignore */ }
    }, [useAI]);

    // Clear stale inference when user edits the feeling text after a suggestion landed.
    // Then, after a short pause, *re-fire* inference quietly so the "sounds like" whisper
    // can render under the textarea (Phase 3·a inline detective). User never has to ask.
    useEffect(() => {
        if (moodLanesSuggestion) setMoodLanesSuggestion(null);
        const trimmed = (feeling || "").trim();
        if (trimmed.length < 10) return;  // too short to whisper meaningfully
        const handle = setTimeout(() => {
            inferPersonalize(trimmed)
                .then((res) => {
                    // Tight guard: only show if confidence is decent and we still hold the same text.
                    if (!res?.suggestion) return;
                    if ((res.suggestion.confidence || 0) < 0.6) return;
                    setMoodLanesSuggestion(res.suggestion);
                })
                .catch(() => { /* silent — the explicit button still works */ });
        }, 900);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feeling]);

    const handleSubmit = () => {
        const trimmed = feeling.trim();
        if (!trimmed) return;
        const ref_code = getStoredRefCode();
        // Reply context — pulled from sessionStorage so the parent slug
        // survives the navigation hand-off. Backend resolves slug → mood_id
        // and stores `reply_to_mood_id` so the thread can be reconstructed.
        let reply_to_slug = null;
        try { reply_to_slug = window.sessionStorage.getItem("moodcard.reply_from_slug") || null; } catch { /* ignore */ }
        track("make_card_click", {
            len: trimmed.length,
            via_lanes: !!moodLanesLabel,
            ai: useAI,
            has_personalize: !!(personalize.audience || personalize.tone || personalize.tags?.length),
            is_reply: !!reply_to_slug,
        });
        navigate("/loading", { state: { feeling: trimmed, personalize, use_ai: useAI, ref_code, reply_to_slug, pack: shapeChoice, challenge_slug: activeChallengeSlug } });
    };

    const handleMoodLanesApply = ({ personalize: p, seed_text, lane_label, voice_label }) => {
        track("mood_lanes_apply", { lane: lane_label, voice: voice_label });
        setPersonalize({ ...personalize, ...p });
        setMoodLanesLabel(`${lane_label} · ${voice_label || ""}`.trim().replace(/·\s*$/, ""));
        if (seed_text) setFeeling(seed_text);
        setMoodLanesSuggestion(null);  // consumed
    };

    const handleKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div className="relative min-h-screen w-full aurora-wash" data-testid="home-screen" data-typing={feeling.trim().length > 0 ? "true" : "false"}>
            {/* Living glow — intensifies subtly as user types. CSS-driven via data-typing attr. */}
            <div className="absolute inset-0 mc-home-glow pointer-events-none" />
            <div className="absolute inset-0 grain pointer-events-none" />

            {/* iter-74 mobile: clean immersive Create studio. Replaces the dense
                desktop Home below on phones. Top bar is MobileTopBar (sticky,
                green-square mark + hamburger + bell). Hamburger sheet absorbs
                all secondary nav (explore, leaderboard, premium, settings…). */}
            <MobileTopBar
                onOpenMenu={() => setHamburgerOpen(true)}
                onOpenNotifications={() => navigate("/me")}
            />
            <HamburgerSheet open={hamburgerOpen} onClose={() => setHamburgerOpen(false)} />
            <MobileCreate
                feeling={feeling}
                setFeeling={setFeeling}
                activeTone={personalize.tone}
                onToneChange={(tone) => setPersonalize({ ...personalize, tone })}
                spice={personalize.spice}
                onSpiceChange={(spice) => setPersonalize({ ...personalize, spice })}
                quota={quota}
                activeChallengeSlug={activeChallengeSlug}
                onClearChallenge={() => setActiveChallengeSlug(null)}
                onSubmit={handleSubmit}
            />

            {/* Legacy desktop Home — wrapped to render only on lg+. Mobile gets
                the new MobileCreate experience above. */}
            <div className="hidden lg:block">

            {/* nav — sticky two-row header in the spirit of the floating
                bottom badges (the same compact-pill rhythm we use for the
                Emergent/MOODCARD chips at the bottom of the page).
                  Row 1: chrome icons RIGHT-aligned (theme · auth · trophy ·
                    dashboard · clock-with-yellow-badge). All h-9 w-9, no
                    background circles, equal rhythm. Left intentionally empty.
                  Row 2: MOODCARD brand pill LEFT-aligned (green-outlined
                    square + tracked wordmark). Right intentionally empty.
                The two rows keep left/right anchoring intact while letting
                the icons breathe at the top and the wordmark live alone
                underneath, museum-clean. */}
            <header
                className="sticky top-0 z-30 backdrop-blur-md max-w-md lg:max-w-3xl mx-auto"
                style={{ backgroundColor: "color-mix(in srgb, var(--surface-1) 78%, transparent)" }}
            >
                {/* iter-81: desktop hamburger menu mirrors mobile. */}
                <div className="px-5 pt-3 pb-0 flex justify-between items-center" data-testid="home-desktop-top-bar">
                    <button
                        onClick={() => setHamburgerOpen(true)}
                        data-testid="home-desktop-menu"
                        title="Menu"
                        aria-label="Open menu"
                        className="flex items-center justify-center h-9 w-9 text-app-2 hover:text-app transition-colors"
                    >
                        <Menu className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                    <span className="text-[10px] tracking-[0.32em] uppercase text-app-3">create</span>
                    <div className="w-9" aria-hidden="true" />
                </div>
                {/* Row 1 — floating icon rail, right-aligned, no chrome */}
                <div className="px-5 pt-1 pb-1 flex justify-end items-center gap-1" data-testid="home-icon-rail">
                    <ThemeToggle naked />
                    <AuthButton compact naked />
                    <button
                        onClick={() => navigate("/explore")}
                        data-testid="home-explore-button"
                        title="Explore — public gallery"
                        aria-label="Explore — public gallery"
                        className="flex items-center justify-center h-9 w-9 text-app-2 hover:text-app transition-colors"
                    >
                        <Compass className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                    <button
                        onClick={() => navigate("/leaderboard")}
                        data-testid="home-leaderboard-button"
                        title="Leaderboard"
                        aria-label="Leaderboard"
                        className="flex items-center justify-center h-9 w-9 text-app-2 hover:text-app transition-colors"
                    >
                        <Trophy className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                    {user && (
                        <button
                            onClick={() => {
                                // Mark the panel as "seen up to now" so the pulse
                                // vanishes for any opens that happened before this tap.
                                try { window.localStorage.setItem("moodcard.dashboard.seen_until", String(Date.now())); } catch { /* ignore */ }
                                setHasFreshOpens(false);
                                navigate("/dashboard");
                            }}
                            data-testid="home-dashboard-button"
                            title={hasFreshOpens ? "Dashboard — someone opened your card" : "Dashboard"}
                            aria-label="Dashboard"
                            className="relative flex items-center justify-center h-9 w-9 text-app-2 hover:text-app transition-colors"
                        >
                            <LayoutDashboard className="w-5 h-5" strokeWidth={1.5} />
                            {hasFreshOpens && (
                                <motion.span
                                    data-testid="dashboard-fresh-pulse"
                                    aria-hidden="true"
                                    animate={{
                                        opacity: [0.6, 1, 0.6],
                                        scale: [0.9, 1.15, 0.9],
                                    }}
                                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                                    className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full"
                                    style={{
                                        background: "#d4af6e",
                                        boxShadow: "0 0 10px rgba(212,175,110,0.65)",
                                    }}
                                />
                            )}
                        </button>
                    )}
                    {historyCount > 0 && (
                        <button
                            onClick={() => navigate("/history")}
                            data-testid="home-history-button"
                            title={`${historyCount} saved`}
                            aria-label={`History — ${historyCount} saved`}
                            className="relative flex items-center justify-center h-9 w-9 text-app-2 hover:text-app transition-colors"
                        >
                            <Clock className="w-5 h-5" strokeWidth={1.5} />
                            {/* Notification count — yellow per spec, floats over the icon */}
                            <span
                                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full font-sans text-[10px] font-semibold flex items-center justify-center"
                                style={{ backgroundColor: "#FACC15", color: "#1a1a0a" }}
                            >
                                {historyCount}
                            </span>
                        </button>
                    )}
                </div>

                {/* Row 2 — brand pill, left-aligned */}
                <div className="px-5 pt-1 pb-3 flex items-center" data-testid="home-brand-row">
                    <div className="flex items-center gap-2" data-testid="home-brand-pill">
                        <span
                            className="flex items-center justify-center h-5 w-5 rounded-[4px]"
                            style={{ border: "1.5px solid #34D399", boxShadow: "0 0 8px rgba(52, 211, 153, 0.18)" }}
                        >
                            <span className="block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#34D399" }} />
                        </span>
                        <span className="font-sans text-xs uppercase tracking-[0.35em] text-app">
                            MOODCARD
                        </span>
                    </div>
                </div>
            </header>

            {/* On this day surfacing — only if a match exists */}
            {onThisDay && (
                <div className="relative z-10 max-w-md lg:max-w-3xl mx-auto px-6 mt-4">
                    <OnThisDay match={onThisDay} />
                </div>
            )}

            {/* main — generous bottom padding on mobile for floating badges;
                tight on desktop so the whole flow fits without scroll. */}
            <main className="relative z-10 max-w-md lg:max-w-6xl mx-auto px-6 pt-6 sm:pt-10 lg:pt-4 pb-56 sm:pb-44 lg:pb-12 flex flex-col">
                {/* Reply context banner — appears when user landed here via
                    "send a feeling back to <Name>" CTA. Soft amber, italic, with
                    a tiny dismiss. The recipient gets pre-filled in the share
                    sheet later in the flow. */}
                {replyTo && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        data-testid="home-reply-banner"
                        className="mb-6 flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-amber-300/30 bg-amber-400/[0.06]"
                    >
                        <div className="flex items-baseline gap-2.5 min-w-0">
                            <span className="font-sans text-[9px] uppercase tracking-[0.42em] text-amber-200/70 shrink-0">
                                replying to
                            </span>
                            <span className="font-serif italic text-amber-100/95 text-[16px] truncate">
                                {replyTo}
                            </span>
                        </div>
                        <button
                            onClick={clearReply}
                            data-testid="home-reply-dismiss"
                            aria-label="Dismiss reply context"
                            className="font-sans text-[9px] uppercase tracking-[0.3em] text-amber-200/60 hover:text-amber-100 transition-colors shrink-0"
                        >
                            clear
                        </button>
                    </motion.div>
                )}

                {/* Desktop-only 2-column layout:
                    LEFT  — headline + textarea + CTA (existing flow, fluid)
                    RIGHT — a floating decorative card preview anchored mid-height
                    On mobile, this is just a single column — `lg:` adds the grid.
                    The right column is purely decorative; it shows the user what
                    a finished MoodCard looks like before they type a word. */}
                <div className="lg:grid lg:grid-cols-[1fr_440px] lg:gap-12 lg:items-start">
                {/* LEFT COLUMN — all form content in one grid cell so it
                    doesn't get split into separate rows that each match
                    the tall right-column card. */}
                <div className="lg:col-start-1">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3 mb-4 lg:mb-3">
                        a quiet place for a loud feeling
                    </p>

                    <h1
                        className="font-serif font-light text-app text-4xl sm:text-5xl lg:text-[44px] leading-[1.05] tracking-tight"
                        data-testid="home-headline"
                    >
                        what are you<br />
                        <span className="italic">feeling</span>
                        <span className="text-app-2"> right now?</span>
                    </h1>

                    <p
                        className="mt-4 lg:mt-3 font-sans text-base lg:text-base text-app-2 leading-relaxed max-w-[30ch]"
                        data-testid="home-subheadline"
                    >
                        Turn your mood into a beautiful card you can post anywhere.
                    </p>
                </motion.div>

                {/* input */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.25, ease: "easeOut" }}
                    className="mt-8 lg:mt-6 relative"
                >
                    {/* Daily quota chip — friendly tone. Amber pulse at remaining===1
                        creates a tiny urgency loop without panic-styling.
                        iter-72 (c): when remaining <= 1 AND user is free-tier, append
                        an inline "upgrade for more" link so the wall isn't a dead end. */}
                    {quota && quota.used > 0 && !isPremium && (
                        <div className="mb-3 flex items-center justify-end gap-2" data-testid="quota-chip">
                            <motion.span
                                animate={
                                    quota.remaining === 1
                                        ? { opacity: [0.85, 1, 0.85], scale: [1, 1.02, 1] }
                                        : { opacity: 1, scale: 1 }
                                }
                                transition={
                                    quota.remaining === 1
                                        ? { duration: 1.8, repeat: Infinity, ease: "easeInOut" }
                                        : { duration: 0.3 }
                                }
                                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border font-sans text-[10px] uppercase tracking-[0.22em] ${
                                    quota.remaining === 0
                                        ? "border-app/25 bg-surface-2 text-app-3"
                                        : quota.remaining <= 1
                                        ? "border-amber-300/40 bg-amber-400/10 text-amber-200"
                                        : "border-app/15 bg-surface-1 text-app-3"
                                }`}
                            >
                                {quota.remaining === 1 ? (
                                    <span className="font-serif italic normal-case tracking-normal text-amber-100/95">
                                        last feeling today
                                    </span>
                                ) : (
                                    <>
                                        <span className="font-medium text-app-2">{quota.remaining}</span>
                                        <span>{quota.remaining === 0 ? "none left today" : "feelings left today"}</span>
                                    </>
                                )}
                            </motion.span>
                            {quota.remaining <= 1 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        track("quota_upgrade_clicked", {
                                            remaining: quota.remaining,
                                            tier: quota.tier,
                                        });
                                        navigate("/?premium=1");
                                    }}
                                    className="font-serif italic text-[12px] text-amber-200/90 hover:text-amber-100 underline underline-offset-2 decoration-amber-300/40 hover:decoration-amber-200 transition-colors"
                                    data-testid="quota-upgrade-link"
                                >
                                    upgrade for more →
                                </button>
                            )}
                        </div>
                    )}
                    {/* iter-72 (e): community challenge tile — collapsible,
                        renders only when active challenges exist. Tapping
                        "write your version" preloads the prompt + tags the
                        resulting card with the challenge slug. */}
                    <ChallengeTile
                        onUsePrompt={(prompt, slug) => {
                            setFeeling(prompt);
                            setActiveChallengeSlug(slug);
                        }}
                    />
                    {activeChallengeSlug && (
                        <div className="mb-3 flex items-center justify-end">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/10 border border-amber-300/25 text-amber-200/90 text-[10px] tracking-[0.18em] uppercase font-medium" data-testid="active-challenge-pill">
                                <Sparkles className="w-3 h-3" />
                                <span>joined</span>
                                <span className="font-serif italic normal-case tracking-normal text-amber-100/95">{activeChallengeSlug}</span>
                                <button
                                    type="button"
                                    onClick={() => setActiveChallengeSlug(null)}
                                    className="ml-1 text-amber-300/55 hover:text-amber-100 transition-colors"
                                    aria-label="leave challenge"
                                >
                                    ×
                                </button>
                            </span>
                        </div>
                    )}
                    <div className="relative rounded-2xl metal-surface p-4 sm:p-5">
                        <textarea
                            value={feeling}
                            onChange={(e) => {
                                const next = e.target.value;
                                setFeeling(next);
                                // Auto-scroll the textarea into view ONCE — the first time
                                // the user actually types. Keeps the headline visible on
                                // load (autoFocus alone would jump it off-screen) but lifts
                                // the editor + CTA into the upper third the moment they
                                // commit to a thought. Mobile-friendly smooth scroll.
                                if (!scrolledRef.current && next.length > 0) {
                                    scrolledRef.current = true;
                                    try { e.target.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { /* ignore */ }
                                }
                            }}
                            onKeyDown={handleKeyDown}
                            maxLength={400}
                            rows={2}
                            data-testid="home-feeling-textarea"
                            placeholder=""
                            className="w-full bg-transparent text-app text-xl sm:text-2xl font-serif font-light leading-snug resize-none outline-none placeholder:text-app-3 min-h-[64px]"
                            autoFocus
                        />
                        {!feeling && (
                            <div className="pointer-events-none absolute top-4 sm:top-5 left-4 sm:left-5 right-4 sm:right-5">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={phIdx}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 0.45, y: 0 }}
                                        exit={{ opacity: 0, y: -6 }}
                                        transition={{ duration: 0.6 }}
                                        className="font-serif italic text-app-3 text-xl sm:text-2xl leading-snug"
                                    >
                                        {ROTATING_PLACEHOLDERS[phIdx]}
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        )}

                        <div className="mt-3 flex items-center justify-between">
                            <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3">
                                {feeling.length}/400
                            </span>
                            <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3 hidden sm:inline">
                                ⌘ + ↵ to make
                            </span>
                        </div>

                        {/* Phase 3·a inline detective — whispers what the AI heard, BEFORE the modal.
                            Tappable: opens the modal with the (already-cached) suggestion pre-lit. */}
                        <AnimatePresence>
                            {moodLanesSuggestion?.lane && (
                                <motion.button
                                    initial={{ opacity: 0, y: -4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -4 }}
                                    transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                                    onClick={() => { track("home_detective_tap", { lane: moodLanesSuggestion.lane }); setMoodLanesOpen(true); }}
                                    data-testid="home-detective-whisper"
                                    className="group mt-3 inline-flex items-center gap-2 text-left -ml-1 px-2 py-1 rounded-md hover:bg-amber-300/5 transition-colors"
                                >
                                    <motion.span
                                        animate={{ opacity: [0.6, 0.95, 0.6] }}
                                        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                                        className="block h-1.5 w-1.5 rounded-full bg-amber-300/80"
                                    />
                                    <span className="font-serif italic text-[13px] text-amber-100/80 leading-tight">
                                        sounds like — <span className="text-amber-100/95">{moodLanesSuggestion.lane}</span>
                                        {moodLanesSuggestion.subMood && <> · {moodLanesSuggestion.subMood.replace(/-/g, " ")}</>}
                                        {moodLanesSuggestion.temperature && <> · {moodLanesSuggestion.temperature.replace(/-/g, " ")}</>}
                                    </span>
                                    <span className="font-sans text-[9px] uppercase tracking-[0.25em] text-amber-200/40 group-hover:text-amber-200/80 transition-colors">
                                        shape ›
                                    </span>
                                </motion.button>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>

                {/* ── Funnel gate ──
                    Per user feedback (iter-68): the visual style / tone /
                    personalize options are MORE useful AFTER the card is
                    generated, where the user can iterate live and see what
                    each option looks like. So the entire panel is HIDDEN
                    here on Home. Defaults are sensible (useAI=true, pack=
                    auto from lane, no personalize) — Make button just works.

                    The block remains in the DOM so testing IDs / state are
                    preserved, but `hidden` keeps it out of layout + a11y.
                */}
                {(() => {
                    const funnelOpen = false; // forced closed; section hidden
                    return (
                <motion.div
                    data-testid="home-funnel-gate"
                    style={{ display: "none" }}
                    className="hidden"
                >

                {/* Inline funnel guidance bubble — disabled in iter-68
                    (funnel gate is gone, all options moved to CardDetail). */}
                {false && !funnelOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="lg:hidden mt-3 -mb-2 flex items-center justify-center"
                        data-testid="home-funnel-hint"
                    >
                        <span className="font-serif italic text-amber-100/85 text-[12px] flex items-center gap-2">
                            <span className="inline-block w-1 h-1 rounded-full bg-amber-300/90 animate-pulse" />
                            write what you feel — the rest follows
                        </span>
                    </motion.div>
                )}

                {/* Shape my mood — step 1 / 2 */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.32 }}
                    className="mt-3"
                >
                    <button
                        onClick={() => {
                            setMoodLanesOpen(true);
                            // Phase 3·a — pre-flight inference. Fire only if user has typed
                            // something substantial; let modal open immediately and pre-light
                            // chips when the suggestion arrives (~200-400ms).
                            const trimmed = (feeling || "").trim();
                            if (trimmed.length >= 6 && !moodLanesSuggestion) {
                                inferPersonalize(trimmed)
                                    .then((res) => { if (res?.suggestion) setMoodLanesSuggestion(res.suggestion); })
                                    .catch(() => { /* silent — modal still works manually */ });
                            }
                        }}
                        data-testid="shape-my-mood-button"
                        className={`btn-press w-full flex items-center justify-between px-4 py-3 rounded-2xl border transition-colors text-left ${
                            moodLanesLabel
                                ? "border-emerald-400/55 bg-emerald-400/10 hover:bg-emerald-400/14"
                                : "border-app/30 bg-surface-1 hover:bg-surface-2"
                        }`}
                    >
                        <div className="flex items-center gap-3">
                            {/* Step indicator — numbered, flips to big green check when complete */}
                            <motion.span
                                key={moodLanesLabel ? "done" : "step"}
                                initial={{ scale: 0.6, rotate: -45 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ type: "spring", stiffness: 380, damping: 18 }}
                                className={`flex items-center justify-center h-7 w-7 rounded-full shrink-0 ${
                                    moodLanesLabel
                                        ? "bg-emerald-400 text-emerald-950 shadow-[0_0_14px_rgba(52,211,153,0.55)]"
                                        : "bg-surface-2 text-app-2 border border-app/30"
                                }`}
                            >
                                {moodLanesLabel ? (
                                    <Check className="w-4 h-4" strokeWidth={3.2} />
                                ) : (
                                    <span className="font-sans text-[11px] font-semibold">1</span>
                                )}
                            </motion.span>
                            <div>
                                <div className={`font-sans text-[10px] uppercase tracking-[0.35em] ${moodLanesLabel ? "text-emerald-200" : "text-app-3"}`}>
                                    {moodLanesLabel ? "shaped" : "shape my mood"}
                                </div>
                                <div className="font-serif italic text-app text-sm mt-0.5">
                                    {moodLanesLabel || "no words? we'll help you find them."}
                                </div>
                            </div>
                        </div>
                        <ArrowRight className={`w-4 h-4 ${moodLanesLabel ? "text-emerald-200" : "text-app-3"}`} strokeWidth={1.5} />
                    </button>

                    {/* Soft affirmation — appears once shape is applied. Fades out after a beat. */}
                    <AnimatePresence>
                        {moodLanesLabel && (
                            <motion.p
                                key="affirm"
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: [0, 1, 1, 0.7], y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 5, times: [0, 0.18, 0.7, 1] }}
                                className="mt-2 ml-10 font-serif italic text-[12px] text-app-3"
                            >
                                okay. that's the shape.
                            </motion.p>
                        )}
                    </AnimatePresence>
                </motion.div>

                {/* personalize — step 2 / 2 */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.35 }}
                    className="mt-3"
                >
                    <PersonalizePanel
                        value={personalize}
                        onChange={setPersonalize}
                        stepNumber={2}
                    />
                </motion.div>

                {/* Style Pack v2 — Shape picker. 4 packs recommended by the
                    inferred lane for free users; full 6-pack library for
                    Premium. Auto-picked when shapeChoice stays null. */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.37 }}
                    className="mt-3"
                >
                    <PackPicker
                        value={shapeChoice}
                        onChange={setShapeChoice}
                        lane={personalize?.lane || moodLanesSuggestion?.lane}
                        isPremium={!!isPremium}
                        onPremiumLocked={() => {
                            navigate("/?premium=1");
                        }}
                    />
                </motion.div>

                {/* AI toggle — reframed as emotional, not technical */}
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.4 }}
                    className="mt-3 flex items-center justify-between px-1"
                >
                    <div className="flex items-center gap-2">
                        <Wand2 className="w-3.5 h-3.5 text-app-3" strokeWidth={1.5} />
                        <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                            {useAI ? "shape the feeling" : "say it raw"}
                        </span>
                    </div>
                    <button
                        onClick={() => setUseAI((v) => !v)}
                        data-testid="ai-toggle"
                        aria-pressed={useAI}
                        aria-label={useAI ? "shape the feeling (on)" : "shape the feeling (off)"}
                        className={`relative h-5 w-9 rounded-full transition-colors ${useAI ? "" : "bg-surface-2"}`}
                        style={{ backgroundColor: useAI ? "var(--app-text)" : undefined }}
                    >
                        <span
                            className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full transition-transform ${useAI ? "translate-x-4" : ""}`}
                            style={{ backgroundColor: useAI ? "var(--app-bg)" : "var(--app-text)" }}
                        />
                    </button>
                </motion.div>

                </motion.div>
                    );
                })()}

                {/* ── Tone chip strip (iter-69) ──
                    User wanted to keep the create flow simple but the LLM
                    benefits from a tone hint. This one-tap row sits right
                    above the Make button — visible on Home (NOT inside the
                    hidden funnel gate). "auto" is selected by default so
                    nothing changes for users who don't engage with it.

                    Tone IDs match the backend `personalize.tone` field.
                */}
                <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.4 }}
                    className="mt-7 lg:mt-5"
                    data-testid="home-tone-strip"
                >
                    <div className="flex items-center justify-between mb-2 px-1">
                        <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3">
                            tone
                        </span>
                        <span className="font-sans text-[9px] uppercase tracking-[0.3em] text-app-3 opacity-65">
                            optional · tap one
                        </span>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
                        {[
                            { id: null,         label: "auto" },
                            { id: "tender",     label: "tender" },
                            { id: "bold",       label: "bold" },
                            { id: "reflective", label: "reflective" },
                            { id: "faith",      label: "faith" },
                            { id: "playful",    label: "playful" },
                        ].map((t) => {
                            const active = (personalize.tone || null) === t.id;
                            return (
                                <button
                                    key={t.label}
                                    type="button"
                                    onClick={() => setPersonalize((p) => ({ ...p, tone: t.id }))}
                                    data-testid={`home-tone-${t.label}`}
                                    data-active={active ? "true" : "false"}
                                    className={`btn-press flex-shrink-0 px-3.5 py-1.5 rounded-full font-sans text-[11px] tracking-wide transition-colors border ${
                                        active
                                            ? "bg-amber-200/15 border-amber-200/65 text-amber-100"
                                            : "border-white/10 bg-white/[0.03] text-app-2 hover:border-white/25 hover:bg-white/[0.06]"
                                    }`}
                                >
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                    {/* iter-78: SPICE dial — desktop parity with MobileCreate.
                        Sits below the tone rail; rose-pink glow on active. */}
                    <div className="mt-4">
                        <div className="text-[10px] tracking-[0.32em] uppercase text-app-3 mb-2 flex items-center gap-2">
                            <span>spice</span>
                            <span className="text-app-3/60 normal-case tracking-normal italic font-serif text-[11px]">
                                how daring should the AI get?
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2" data-testid="home-spice-rail">
                            {[
                                { key: "mild",     label: "Mild",     sub: "gentle" },
                                { key: "bold",     label: "Bold",     sub: "default" },
                                { key: "chaotic",  label: "Chaotic",  sub: "internet-coded" },
                                { key: "unhinged", label: "Unhinged", sub: "full delulu" },
                            ].map((s) => {
                                const isActive = (personalize.spice || "bold") === s.key;
                                return (
                                    <button
                                        key={s.key}
                                        type="button"
                                        onClick={() => setPersonalize((p) => ({ ...p, spice: s.key }))}
                                        data-testid={`home-spice-${s.key}`}
                                        data-active={isActive ? "true" : "false"}
                                        className={`btn-press px-3.5 py-1.5 rounded-2xl font-sans text-left transition-all border ${
                                            isActive
                                                ? "bg-rose-400/10 border-rose-300/55 text-rose-100 shadow-[0_0_16px_-4px_rgba(244,114,182,0.4)]"
                                                : "border-white/10 bg-white/[0.03] text-app-2 hover:border-white/25 hover:bg-white/[0.06]"
                                        }`}
                                    >
                                        <div className="text-[12px] font-medium leading-tight">{s.label}</div>
                                        <div className="text-[9px] tracking-[0.18em] uppercase opacity-60 mt-0.5">
                                            {s.sub}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </motion.div>

                {/* button — breathes / glows once user has typed enough to feel intentional */}
                {(() => {
                    const ready = feeling.trim().length >= 8;            // textarea has real content
                    const prepped = !!moodLanesLabel || (personalize?.audience || personalize?.tone || (personalize?.tags || []).length > 0);
                    const inviting = ready;                              // breathe whenever feeling is meaningful
                    const fullyPrepped = ready && prepped;               // extra glow if they also picked shape/personalize
                    return (
                        <motion.button
                            initial={{ opacity: 0, y: 12 }}
                            animate={
                                inviting
                                    ? {
                                        opacity: 1,
                                        y: [-6, -10, -6],          // floats up so the thumb finds it at the lower-middle
                                        scale: [1, 1.018, 1],
                                        boxShadow: fullyPrepped
                                            ? [
                                                "0 10px 28px -10px rgba(0,0,0,0.45), 0 0 0 rgba(110,231,183,0)",
                                                "0 18px 40px -8px rgba(0,0,0,0.55), 0 0 34px rgba(110,231,183,0.55)",
                                                "0 10px 28px -10px rgba(0,0,0,0.45), 0 0 0 rgba(110,231,183,0)",
                                            ]
                                            : [
                                                "0 10px 28px -10px rgba(0,0,0,0.45), 0 0 0 rgba(255,255,255,0)",
                                                "0 16px 36px -8px rgba(0,0,0,0.55), 0 0 22px rgba(255,255,255,0.22)",
                                                "0 10px 28px -10px rgba(0,0,0,0.45), 0 0 0 rgba(255,255,255,0)",
                                            ],
                                    }
                                    : { opacity: 1, y: 0 }
                            }
                            transition={{
                                opacity: { duration: 0.6, delay: 0.45 },
                                y: { duration: 0.6, delay: 0.45 },
                                scale: { duration: 2.6, repeat: inviting ? Infinity : 0, ease: "easeInOut" },
                                boxShadow: { duration: 2.6, repeat: inviting ? Infinity : 0, ease: "easeInOut" },
                            }}
                            whileTap={{ scale: 0.97 }}
                            whileHover={{ scale: 1.02 }}
                            onClick={handleSubmit}
                            disabled={!feeling.trim()}
                            data-testid="home-make-button"
                            className="btn-press btn-metal mt-5 mx-auto group relative flex items-center justify-center gap-2 py-2.5 px-5 rounded-full font-sans font-medium text-xs tracking-wide disabled:opacity-30 disabled:cursor-not-allowed"
                            style={{ width: "auto", maxWidth: "260px" }}
                        >
                            <Sparkles className="w-3.5 h-3.5 relative z-10" strokeWidth={1.5} />
                            <span className="relative z-10">Make my MoodCard</span>
                            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 relative z-10" strokeWidth={1.5} />
                        </motion.button>
                    );
                })()}

                <p className="mt-3 text-center lg:text-left font-sans text-[10px] tracking-wide text-app-3">
                    no signup required. just you and the feeling.
                </p>

                {/* Card of the Day — mobile slim tile. Shows below the
                    primary CTA so it's discovered AFTER the user can
                    make their own card. Hidden on lg+ (desktop aside
                    below handles that). Hidden entirely when the API
                    returns no card. */}
                <CardOfTheDay variant="mobile-tile" />

                {/* Creator Studio hook — surfaces brand customization
                    (iter-71). Free users would never find this otherwise.
                    Single soft line, no aggressive CTA, taps through to
                    /creators where they see what real creators are doing
                    with their cards. Conversion happens through aspiration,
                    not nag. */}
                <motion.button
                    type="button"
                    onClick={() => navigate("/creators")}
                    data-testid="home-creator-studio-hook"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.7 }}
                    className="btn-press mt-5 lg:mt-4 mx-auto block text-center group"
                >
                    <span className="font-serif italic text-amber-100/85 text-[13px] group-hover:text-amber-200 transition-colors">
                        want your brand on every card?
                    </span>
                    <span className="ml-2 font-sans text-[9px] uppercase tracking-[0.35em] text-app-3 group-hover:text-amber-200/85 transition-colors">
                        see creator studio →
                    </span>
                </motion.button>
                </div>{/* end LEFT column wrapper */}

                {/* Card of the Day — desktop aside. REAL recent card
                    refreshed daily. Replaces the legacy static decorative
                    preview. Hidden on mobile. Hidden entirely when the
                    API returns no card. */}
                <CardOfTheDay variant="desktop-aside" />
                </div>{/* end lg:grid wrapper */}

                {/* Referral card — only when logged in */}
                {user && (
                    <div className="mt-8">
                        <ReferralCard />
                    </div>
                )}

                {/* Legal footer — quiet single line near the bottom of Home.
                    Provides corporate accountability without breaking the
                    museum aesthetic. Zephiron Group LLC is the registered
                    operator (Wyoming, USA). Two small links below. */}
                <footer className="mt-16 mb-2 text-center" data-testid="home-legal-footer">
                    <button
                        onClick={() => navigate("/about")}
                        data-testid="home-footer-about-link"
                        className="btn-press font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 opacity-65 hover:opacity-100 transition-opacity"
                    >
                        moodcard · a product of <span className="text-app-2">zephiron group llc</span> · wyoming, usa
                    </button>
                    <div className="mt-2 flex items-center justify-center gap-4">
                        <button
                            onClick={() => navigate("/privacy")}
                            data-testid="home-footer-privacy-link"
                            className="btn-press font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 opacity-55 hover:opacity-100 transition-opacity"
                        >
                            privacy
                        </button>
                        <span className="text-app-3 opacity-30 text-[9px]" aria-hidden="true">·</span>
                        <button
                            onClick={() => navigate("/terms")}
                            data-testid="home-footer-terms-link"
                            className="btn-press font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 opacity-55 hover:opacity-100 transition-opacity"
                        >
                            terms
                        </button>
                        <span className="text-app-3 opacity-30 text-[9px]" aria-hidden="true">·</span>
                        <a
                            href="mailto:hello@moodcard.app"
                            data-testid="home-footer-contact-link"
                            className="btn-press font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 opacity-55 hover:opacity-100 transition-opacity"
                        >
                            contact
                        </a>
                    </div>
                </footer>
            </main>

            <div className="pointer-events-none fixed bottom-20 sm:bottom-24 left-0 right-0 text-center z-10">
                <span className="font-serif italic text-[11px] text-app-3 opacity-70">
                    "say it once. say it beautifully."
                </span>
            </div>

            </div>{/* /lg:block legacy desktop Home wrapper (iter-74) */}

            <MoodLanes
                open={moodLanesOpen}
                onOpenChange={(v) => { setMoodLanesOpen(v); if (!v) setMoodLanesSuggestion(null); }}
                onApply={handleMoodLanesApply}
                suggestion={moodLanesSuggestion}
            />
        </div>
    );
}
